package diana.soleil.design.iraniancanadienbazzar.core.firebase

import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsTracker
import diana.soleil.design.iraniancanadienbazzar.core.common.AppPreferences
import diana.soleil.design.iraniancanadienbazzar.core.common.CrashReporter
import diana.soleil.design.iraniancanadienbazzar.core.common.CrashReporting
import diana.soleil.design.iraniancanadienbazzar.core.common.NoOpAnalyticsTracker
import diana.soleil.design.iraniancanadienbazzar.core.common.NoOpCrashReporter
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.analytics.analytics
import org.koin.dsl.module

/**
 * Firebase-backed telemetry (GitLive). Analytics exists on all three targets; Crashlytics has no web
 * SDK, so [platformCrashReporter] is a no-op on JS (expect/actual). Bound by the composition root via
 * [firebaseTelemetryModule] only when Firebase is initialized for the platform ([noOpTelemetryModule]
 * otherwise — previews/fakes emit nothing).
 */
internal class FirebaseAnalyticsTracker : AnalyticsTracker {
    // Lazy so constructing the tracker (Koin startup) never touches Firebase before platform init.
    private val analytics get() = Firebase.analytics

    override fun track(event: String, params: Map<String, Any>) {
        runCatching { analytics.logEvent(event, params.ifEmpty { null }) }
    }

    override fun setUserId(uid: String?) {
        runCatching { analytics.setUserId(uid) }
    }
}

/**
 * Consent gate for behavioural analytics (Quebec Law 25 privacy-by-default): forwards events to
 * [delegate] ONLY when the user has opted in ([AppPreferences.analyticsConsent], default false).
 * Read fresh on every call so toggling consent in Profile takes effect immediately. Crash reporting is
 * app-stability data and is not gated here.
 */
internal class ConsentGatedAnalyticsTracker(
    private val delegate: AnalyticsTracker,
    private val preferences: AppPreferences,
) : AnalyticsTracker {
    override fun track(event: String, params: Map<String, Any>) {
        if (preferences.analyticsConsent) delegate.track(event, params)
    }

    override fun setUserId(uid: String?) {
        // Clear the id downstream on revoke; only set it when consented.
        delegate.setUserId(if (preferences.analyticsConsent) uid else null)
    }
}

/** Crashlytics on Android/iOS; no-op on web (no Crashlytics JS SDK). */
internal expect fun platformCrashReporter(): CrashReporter

val firebaseTelemetryModule = module {
    single<AnalyticsTracker> { ConsentGatedAnalyticsTracker(FirebaseAnalyticsTracker(), get()) }
    // Also published to the CrashReporting holder so sub-DI layers (repository error mapper)
    // can record non-fatals — caught errors were previously invisible in Crashlytics.
    single<CrashReporter> { platformCrashReporter().also { CrashReporting.reporter = it } }
}

val noOpTelemetryModule = module {
    single<AnalyticsTracker> { NoOpAnalyticsTracker }
    single<CrashReporter> { NoOpCrashReporter }
}
